import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { oauthHandoffStore } from "./oauthHandoffStore";
import { parseOAuthBridgeUrl } from "./parseOAuthBridgeUrl";
import { oauthNativeResult, type OAuthNativeResultDetail } from "./oauthNativeResult";
import { getAuthOauthHandoff } from "../endpoints/auth/oauth_handoff_GET.schema";
import { postEstablishSession } from "../endpoints/auth/establish_session_POST.schema";
import { getSession } from "../endpoints/auth/session_GET.schema";

let isAttached = false;
let inFlight = false;
let completed = false;
let pollInterval: ReturnType<typeof setInterval> | null = null;
let lastResult: OAuthNativeResultDetail | null = null;
const processedUrls = new Set<string>();

const POLL_INTERVAL_MS = 1500;
const TIMEOUT_MS = 180000; // 3 minutes

function stopPolling() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}

function startPolling() {
  stopPolling();
  pollInterval = setInterval(() => {
    pollOnce();
  }, POLL_INTERVAL_MS);
}

function triggerImmediatePoll() {
  if (!inFlight || completed) return;
  console.log("[oauth-native] foreground trigger, polling immediately");
  startPolling(); // restarts interval so we don't double-fire if interval was about to hit
  pollOnce();
}

function dispatchSuccess(user: any, alreadyCompleted = false) {
  if (!alreadyCompleted) {
    completed = true;
    stopPolling();
  }
  oauthHandoffStore.clear();
  inFlight = false;
  const detail: OAuthNativeResultDetail = { status: "success", user };
  lastResult = detail;
  oauthNativeResult.dispatch(detail);
}

function dispatchError(message: string, code?: string, details?: string, alreadyCompleted = false) {
  if (!alreadyCompleted) {
    completed = true;
    stopPolling();
  }
  console.error(`[oauth-native] error dispatched: ${message}`);
  oauthHandoffStore.clear();
  inFlight = false;
  const detail: OAuthNativeResultDetail = { status: "error", message, code, details };
  lastResult = detail;
  oauthNativeResult.dispatch(detail);
}

async function reconcileAndGiveUp(fallbackErrorMessage: string, alreadyCompleted = false): Promise<void> {
  if (!alreadyCompleted) {
    if (completed) return;
    completed = true;
    stopPolling();
  }
  console.log("[oauth-native] attempting session reconciliation");
  try {
    const sessionResult = await getSession();
    if (!("error" in sessionResult) && sessionResult.user) {
      console.log("[oauth-native] reconciliation successful");
      dispatchSuccess(sessionResult.user, true);
      return;
    }
  } catch (e) {
    console.error("[oauth-native] reconciliation network error:", e);
  }
  
  console.log("[oauth-native] reconciliation failed, giving up");
  dispatchError(fallbackErrorMessage, undefined, undefined, true);
}

async function completeWithTempToken(tempToken: string, source: string): Promise<void> {
  if (completed) return;
  
  // Single completion guard: mark completed immediately so duplicate URLs or racing polls abort
  completed = true;
  stopPolling();
  console.log(`[oauth-native] completing via ${source}`);
  
  try {
    const result = await postEstablishSession({ tempToken });

    if ("error" in result) {
      console.error(`[oauth-native] establish_session failed: ${result.error}`);
      await reconcileAndGiveUp("Failed to complete authentication. Please try again.", true);
      return;
    }

    dispatchSuccess(result.user, true);
  } catch (error) {
    console.error("[oauth-native] establish_session network error:", error);
    await reconcileAndGiveUp("An unexpected error occurred during authentication. Please try again.", true);
  }
}

async function pollOnce(): Promise<void> {
  if (completed) return;
  if (!inFlight) return;

  const handoff = oauthHandoffStore.get();
  if (!handoff) {
    console.error("[oauth-native] handoff vanished while in flight");
    await reconcileAndGiveUp("Session flow vanished.");
    return;
  }

  // Use elapsed wall-clock time so background timer freezing does not break the cap
  if (Date.now() - handoff.createdAt > TIMEOUT_MS) {
    console.error("[oauth-native] 3-minute wall-clock cap reached");
    await reconcileAndGiveUp("Sign-in timed out. Please try again.");
    return;
  }

  try {
    const result = await getAuthOauthHandoff({ handoffId: handoff.id });

    if (completed) return;

    if (result.status === "ready") {
      console.log(`[oauth-native] poll result: ready`);
      if (result.provider && result.provider !== handoff.provider) {
        console.error(`[oauth-native] provider mismatch: handoff provider is ${handoff.provider}, but poll returned ${result.provider}`);
      }
      await completeWithTempToken(result.tempToken, "poll");
    } else if (result.status === "error") {
      console.log(`[oauth-native] poll result: error`);
      if (result.provider && result.provider !== handoff.provider) {
        console.error(`[oauth-native] provider mismatch: handoff provider is ${handoff.provider}, but poll returned ${result.provider}`);
      }
      dispatchError(result.message, result.code, result.details);
    } else if (result.status === "expired") {
      console.log(`[oauth-native] poll result: expired`);
      await reconcileAndGiveUp("Sign-in session expired. Please try again.");
    }
  } catch (error) {
    console.error("[oauth-native] poll network error:", error);
    // Do not stop polling or complete the flow, let it keep trying until the wall-clock cap
  }
}

function handleUrl(url: string) {
  if (!url || processedUrls.has(url)) return;
  
  const message = parseOAuthBridgeUrl(url);
  if (!message) return;
  
  processedUrls.add(url);
  console.log("[oauth-native] deep link received");
  
  if (message.type === "OAUTH_TOKEN_SUCCESS") {
    completeWithTempToken(message.token, "deeplink");
  } else if (message.type === "OAUTH_ERROR") {
    console.error(`[oauth-native] error from deep link: ${message.error.message}`);
    dispatchError(
      message.error.message || "Authentication failed. Please try again.",
      message.error.code,
      message.error.details
    );
  }
}

export const oauthNativeEngine = {
  beginAttempt(provider: string): string {
    console.log(`[oauth-native] attempt begin for ${provider}`);
    processedUrls.clear();
    inFlight = true;
    completed = false;
    lastResult = null;
    
    const handoffId = oauthHandoffStore.start(provider);
    
    this.ensureAttached();
    startPolling();
    pollOnce(); // Kick off first poll immediately
    
    return handoffId;
  },

  resumePendingAttempt(): void {
    if (inFlight || completed) return;
    
    const handoff = oauthHandoffStore.get();
    if (!handoff) return;
    
    if (Date.now() - handoff.createdAt > TIMEOUT_MS) {
      oauthHandoffStore.clear();
      return;
    }
    
    console.log(`[oauth-native] resuming pending attempt for ${handoff.provider}`);
    inFlight = true;
    completed = false;
    lastResult = null;
    processedUrls.clear();
    
    this.ensureAttached();
    startPolling();
    pollOnce();
  },

  ensureAttached(): void {
    if (isAttached) return;
    isAttached = true;
    console.log("[oauth-native] attach listeners");
    this.resumePendingAttempt();

    const safeAddListener = (name: string, fn: any) => {
      try {
        (App as any).addListener(name, fn).catch((err: any) => {
          console.error(`[oauth-native] failed to add ${name} listener`, err);
        });
      } catch (err) {
        console.error(`[oauth-native] failed to add ${name} listener`, err);
      }
    };

    if (Capacitor.isNativePlatform()) {
      safeAddListener("appUrlOpen", (data: any) => {
        handleUrl(data.url);
      });

      safeAddListener("resume", () => {
        console.log("[oauth-native] app resumed");
        triggerImmediatePoll();
      });

      safeAddListener("appStateChange", (state: any) => {
        if (state.isActive) {
          console.log("[oauth-native] app state active");
          triggerImmediatePoll();
        }
      });

      try {
        App.getLaunchUrl().then((launchUrl) => {
          if (launchUrl && launchUrl.url) {
            handleUrl(launchUrl.url);
          }
        }).catch(err => {
          console.error("[oauth-native] failed to get launch url", err);
        });
      } catch (err) {
        console.error("[oauth-native] failed to get launch url", err);
      }
    }

    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        console.log("[oauth-native] document visible");
        triggerImmediatePoll();
      }
    });
  },

  hasAttemptInFlight(): boolean {
    return inFlight;
  },

  consumePendingResult(): OAuthNativeResultDetail | null {
    const result = lastResult;
    lastResult = null;
    return result;
  },

  async reconcileNow(): Promise<void> {
    if (completed) return;

    const handoff = oauthHandoffStore.get();
    if (!inFlight && !handoff) {
      console.log("[oauth-native] forcing immediate reconciliation for idle engine");
      try {
        const sessionResult = await getSession();
        if (!("error" in sessionResult) && sessionResult.user) {
          console.log("[oauth-native] reconciliation successful");
          dispatchSuccess(sessionResult.user, false);
        }
      } catch (e) {
        console.error("[oauth-native] reconciliation network error:", e);
      }
      return;
    }

    console.log("[oauth-native] forcing immediate reconciliation");
    
    await pollOnce();
    
    if (!completed) {
      await reconcileAndGiveUp("Sign-in timed out. Please try again.");
    }
  }
};